import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { CalendarClock, ListOrdered, Play } from 'lucide-react';
import { JOB_BULK_ACTIONS, JOB_STATUSES, JOB_TRIGGERS, bulkJobs, startJob } from '@mkcore/platform/schema';
import {
  BulkBar,
  Button,
  buttonVariants,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Mono,
  NativeSelect,
  PageHeader,
  SelectAllCheckbox,
  SelectRowCheckbox,
  Skeleton,
  TBody,
  THead,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
  toast,
  useSelection,
} from '@mkcore/ui';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { jobTypesQuery, jobsQuery } from '@/features/admin/queries';
import { organisationsQuery } from '@/features/crm/queries';
import { JobStatusBadge, formatDuration } from '@/features/admin/job-status';

const statuses = JOB_STATUSES.filter((st) => st !== 'unknown');
const search = z.object({
  status: z.enum(JOB_STATUSES).optional(),
  trigger: z.enum(JOB_TRIGGERS).optional(),
  jobType: z.string().optional(),
  organisationId: z.string().optional(),
  tag: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  open: z.boolean().optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/admin/jobs/')({ validateSearch: search, component: Jobs });

/** A date field (yyyy-mm-dd) as the start of that day in the browser's zone, for the period filter. */
const dayStart = (d?: string) => (d ? new Date(`${d}T00:00:00`).toISOString() : undefined);
const dayAfter = (d?: string) => (d ? new Date(new Date(`${d}T00:00:00`).getTime() + 86_400_000).toISOString() : undefined);

/** Run history with filters and bulk actions (CO-0965, CO-0964, CO-0959). */
function Jobs() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const message = useErrorMessage();
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const q = useInfiniteQuery(
    jobsQuery({
      status: s.status ? [s.status] : undefined,
      trigger: s.trigger ? [s.trigger] : undefined,
      jobType: s.jobType || undefined,
      organisationId: s.organisationId || undefined,
      tag: s.tag || undefined,
      from: dayStart(s.from),
      to: dayAfter(s.to),
      openFailures: s.open || undefined,
    }),
  );
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const ids = useMemo(() => rows.map((r) => r.workflowId), [rows]);
  const selection = useSelection(ids);
  const types = useQuery(jobTypesQuery);
  const orgs = useInfiniteQuery(organisationsQuery({ limit: 200 }));
  const orgName = new Map((orgs.data?.pages.flatMap((p) => p.items) ?? []).map((o) => [o.id, o.name] as const));
  const canUpdate = useCan('admin:update');
  const [starting, setStarting] = useState(false);
  const bulk = useActionMutation(bulkJobs, {
    invalidate: [['admin', 'job']],
    onSuccess: (r) => {
      const ok = r.results.filter((x) => x.ok).length;
      toast.success(t('jobs.bulk.done', { ok, failed: r.results.length - ok }));
      selection.clear();
    },
  });
  const knownTypes = [...new Set([...(types.data?.items.map((j) => j.type) ?? []), ...rows.map((r) => r.jobType), ...(s.jobType ? [s.jobType] : [])])].sort();
  return (
    <>
      <PageHeader
        eyebrow={t('nav.admin')}
        title={t('jobs.title')}
        description={t('jobs.description')}
        actions={
          <>
            <Link to="/admin/jobs/queue" className={buttonVariants({ variant: 'secondary' })}>
              <ListOrdered /> {t('jobs.queue')}
            </Link>
            <Link to="/admin/schedules" className={buttonVariants({ variant: 'secondary' })}>
              <CalendarClock /> {t('jobs.schedules')}
            </Link>
            {canUpdate && (
              <Button onClick={() => setStarting(true)}>
                <Play /> {t('jobs.start')}
              </Button>
            )}
          </>
        }
      />
      <StartJobDialog open={starting} onClose={() => setStarting(false)} />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('jobs.type')} className="w-56">
          {(p) => (
            <NativeSelect {...p} value={s.jobType ?? ''} onChange={(e) => set({ jobType: e.target.value || undefined })}>
              <option value="">{t('common.all')}</option>
              {knownTypes.map((jt) => (
                <option key={jt} value={jt}>
                  {jt}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('common.status')} className="w-40">
          {(p) => (
            <NativeSelect {...p} value={s.status ?? ''} onChange={(e) => set({ status: (e.target.value || undefined) as Search['status'] })}>
              <option value="">{t('common.all')}</option>
              {statuses.map((st) => (
                <option key={st} value={st}>
                  {t(`jobs.status.${st}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('jobs.trigger')} className="w-40">
          {(p) => (
            <NativeSelect {...p} value={s.trigger ?? ''} onChange={(e) => set({ trigger: (e.target.value || undefined) as Search['trigger'] })}>
              <option value="">{t('common.all')}</option>
              {JOB_TRIGGERS.map((tr) => (
                <option key={tr} value={tr}>
                  {t(`jobs.triggers.${tr}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('jobs.client')} className="w-48">
          {(p) => (
            <NativeSelect {...p} value={s.organisationId ?? ''} onChange={(e) => set({ organisationId: e.target.value || undefined })}>
              <option value="">{t('common.all')}</option>
              {[...orgName.entries()].map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('jobs.tag')} className="w-36">
          {(p) => <Input {...p} defaultValue={s.tag ?? ''} onBlur={(e) => set({ tag: e.target.value.trim() || undefined })} />}
        </Field>
        <Field label={t('jobs.from')} className="w-40">
          {(p) => <Input {...p} type="date" value={s.from ?? ''} onChange={(e) => set({ from: e.target.value || undefined })} />}
        </Field>
        <Field label={t('jobs.to')} className="w-40">
          {(p) => <Input {...p} type="date" value={s.to ?? ''} onChange={(e) => set({ to: e.target.value || undefined })} />}
        </Field>
        <label className="flex h-10 items-center gap-2 text-sm">
          <Checkbox checked={!!s.open} onCheckedChange={(v) => set({ open: v === true || undefined })} />
          {t('jobs.openFailures')}
        </label>
      </div>
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-64" />
      ) : !rows.length ? (
        <EmptyState title={t('jobs.empty')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              {canUpdate && (
                <Th className="w-8">
                  <SelectAllCheckbox selection={selection} label={t('common.selectAll')} />
                </Th>
              )}
              <Th>{t('jobs.type')}</Th>
              <Th>{t('common.status')}</Th>
              <Th className="hidden md:table-cell">{t('jobs.trigger')}</Th>
              <Th className="hidden lg:table-cell">{t('jobs.target')}</Th>
              <Th className="hidden md:table-cell text-right">{t('jobs.duration')}</Th>
              <Th>{t('common.created')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((j) => (
              <Tr key={j.workflowId} data-selected={selection.isSelected(j.workflowId)}>
                {canUpdate && (
                  <Td>
                    <SelectRowCheckbox selection={selection} rowKey={j.workflowId} label={t('jobs.bulk.select', { id: j.workflowId })} />
                  </Td>
                )}
                <Td>
                  <Link to="/admin/jobs/$id" params={{ id: j.workflowId }} className="font-bold hover:underline">
                    {j.jobType}
                  </Link>
                  <div>
                    <Mono className="text-[11px]">{j.workflowId}</Mono>
                  </div>
                  {j.tags.length > 0 && <div className="mt-0.5 text-[11px] text-fg-3">{j.tags.join(' · ')}</div>}
                </Td>
                <Td>
                  <JobStatusBadge status={j.status} outcome={j.outcome} stuck={!!j.stuckAt} resolved={!!j.resolvedAt} />
                  {j.error && <div className="mt-1 max-w-xs truncate text-xs text-danger">{j.error}</div>}
                </Td>
                <Td className="hidden md:table-cell text-fg-2">{t(`jobs.triggers.${j.trigger}`, { defaultValue: j.trigger })}</Td>
                <Td className="hidden lg:table-cell text-xs text-fg-2">
                  {j.organisationId && <div>{orgName.get(j.organisationId) ?? ''}</div>}
                  {j.targetType && (
                    <Mono className="text-[11px]">
                      {j.targetType} {j.targetId}
                    </Mono>
                  )}
                </Td>
                <Td className="hidden md:table-cell text-right tabular text-fg-2">{formatDuration(j.durationMs)}</Td>
                <Td className="whitespace-nowrap tabular text-fg-2">{formatDateTime(j.createdAt)}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      {q.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
      <BulkBar count={selection.count} label={t('jobs.bulk.selected', { count: selection.count })} clearLabel={t('common.clearSelection')} onClear={selection.clear}>
        {JOB_BULK_ACTIONS.map((action) => (
          <Button key={action} size="sm" variant="secondary" loading={bulk.isPending && bulk.variables?.input.action === action} onClick={() => bulk.mutate({ input: { action, ids: [...selection.selected] } })}>
            {t(`jobs.bulk.${action}`)}
          </Button>
        ))}
      </BulkBar>
    </>
  );
}

/** Start a job by hand (CO-0710): pick a type, give its input as JSON, follow the run in the list. */
function StartJobDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const types = useQuery({ ...jobTypesQuery, enabled: open });
  const [type, setType] = useState('');
  const [input, setInput] = useState('{}');
  const [error, setError] = useState<string | null>(null);
  const message = useErrorMessage();
  const start = useActionMutation(startJob, {
    invalidate: [['admin', 'job']],
    silentErrors: true,
    onSuccess: (r) => {
      onClose();
      void navigate({ to: '/admin/jobs/$id', params: { id: r.workflowId } });
    },
  });
  const chosen = types.data?.items.find((j) => j.type === type) ?? types.data?.items[0];
  const submit = () => {
    let parsed: Record<string, unknown>;
    try {
      parsed = input.trim() ? (JSON.parse(input) as Record<string, unknown>) : {};
    } catch {
      setError(t('jobs.inputInvalid'));
      return;
    }
    if (!chosen) return;
    setError(null);
    start.mutate({ input: { jobType: chosen.type, input: parsed } }, { onError: (e) => setError(message(e)) });
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('jobs.start')}</DialogTitle>
          {chosen && <DialogDescription>{chosen.description}</DialogDescription>}
        </DialogHeader>
        <div className="grid gap-3">
          <Field label={t('jobs.type')}>
            {(p) => (
              <NativeSelect {...p} value={chosen?.type ?? ''} onChange={(e) => setType(e.target.value)}>
                {(types.data?.items ?? []).map((j) => (
                  <option key={j.type} value={j.type}>
                    {j.title}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label={t('jobs.input')} hint={t('jobs.inputHint')} error={error ?? undefined}>
            {(p) => <Textarea {...p} rows={4} value={input} onChange={(e) => setInput(e.target.value)} className="font-mono text-xs" />}
          </Field>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button loading={start.isPending} disabled={!chosen} onClick={submit}>
            <Play /> {t('jobs.startNow')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
